/**
 * Centralized Theme Configuration
 * 
 * This file defines all theme-related constants including text colors.
 * Use these constants throughout the application for consistent styling.
 */

/**
 * Text Color Classes
 * 
 * Use these standardized text color classes across the entire application:
 * - text.primary: Main content text (headings, important data)
 * - text.secondary: Supporting text (labels, descriptions, metadata)
 * - text.tertiary: Subtle text (hints, placeholders, disabled states)
 * - text.inverse: Text on dark backgrounds
 */
export const text = {
  /** Primary text color for headings, main content, and important data */
  primary: 'text-gray-900',
  
  /** Secondary text color for labels, descriptions, and supporting content */
  secondary: 'text-gray-700',
  
  /** Tertiary text color for subtle content, hints, and metadata */
  tertiary: 'text-gray-500',
  
  /** Text color for content on dark backgrounds */
  inverse: 'text-white',
  
  /** Text color for errors and warnings */
  error: 'text-red-800',
  warning: 'text-yellow-800',
  success: 'text-green-800',
  info: 'text-blue-800',
} as const;

/**
 * Interactive Text Colors
 * Text colors that change on hover/focus
 */
export const textInteractive = {
  /** Navigation links and interactive text */
  link: 'text-gray-500 hover:text-gray-700',
  
  /** Clickable/interactive secondary text */
  clickable: 'text-gray-600 hover:text-gray-900',
} as const;

/**
 * Background Colors for Text Readability
 * Recommended background colors to ensure text is readable
 */
export const textBackgrounds = {
  /** Light backgrounds for primary/secondary text */
  light: 'bg-white',
  lightAlt: 'bg-gray-50',
  
  /** Dark backgrounds for inverse text */
  dark: 'bg-gray-900',
  darkAlt: 'bg-gray-800',
  
  /** Colored backgrounds for status messages */
  error: 'bg-red-50',
  warning: 'bg-yellow-50',
  success: 'bg-green-50',
  info: 'bg-blue-50',
} as const;

/**
 * Helper function to get text color by type
 */
export function getTextColor(type: keyof typeof text): string {
  return text[type];
}

/**
 * Helper function to get interactive text color
 */
export function getInteractiveTextColor(type: keyof typeof textInteractive): string {
  return textInteractive[type];
}
